import * as yup from 'yup'

import { type GremlinClientFlagsPartial } from 'components/LaunchDarklyProvider'
import {
  isSelectedObservabilityToolAuthenticated,
  type ObservabilityTool
} from 'components/status-checks/authentication-form-utils'
import { isObservabilityToolOfType } from 'components/status-checks/authentication-form-validation-utils'
import {
  DEFAULT_POLL_INTERVAL,
  MAXIMUM_POLL_INTERVAL,
  MINIMUM_POLL_INTERVAL
} from 'components/status-checks/common/PollingIntervalField'
import { DynatraceEntitySelectionSource } from 'components/status-checks/form/status-check/dynatrace/DynatraceMonitorSelection'
import { type GrafanaSelectionSource } from 'components/status-checks/form/status-check/grafana/GrafanaMonitorSelection'
import { PrometheusSelectionSource } from 'components/status-checks/form/status-check/prometheus/prometheus-monitor-utils'
import { StatusCheckCategoryFieldVisibilityState } from 'components/status-checks/hooks/use-get-category-field-visibility-state'
import {
  MAX_OK_LATENCY_MS,
  MIN_OK_LATENCY_MS,
  type StatusCheckFormValues
} from 'components/status-checks/status-checks-form-utils'
import {
  extractUrlsFromStatusChecks,
  getExistingMonitorUrlPredicateFn,
  getExistingMonitorUrlTransformFn,
  validateMonitorUrl
} from 'components/status-checks/status-checks-form-validation-utils'
import {
  type EndpointConfiguration,
  ObservabilityToolType,
  type PredicateType,
  type ServiceStatusCheckFromStatusCheck,
  StatusCheckCategory,
  type StatusCheckEvaluationPredicate,
  type TeamExternalIntegration
} from 'types/api/status-checks/status-check'
import { dynatraceEntityIdRegex } from 'utils/status-checks/status-check-integrations-util-dynatrace'
import { isValidStatusCodeOrRange } from 'utils/team-external-integrations/team-external-integrations-util'

/*
 * Validation Schemas
 */
const statusCheckEvaluationPredicateSchema: yup.SchemaOf<StatusCheckEvaluationPredicate> =
  yup.object({
    type: yup.mixed<PredicateType>().required(),
    jqQuery: yup.string(),
    jpQuery: yup.string(),
    comparator: yup.string(),
    rValue: yup.mixed()
  })

const responseBodyEvaluationSchema = yup.object({
  op: yup.mixed<'AND' | 'OR'>().oneOf(['AND', 'OR']).required(),
  predicates: yup.array().of(statusCheckEvaluationPredicateSchema).required()
})

const healthyStatusCodeSchema = yup
  .string()
  .test(
    'is-healthy-status-code-valid',
    'Use a single status code (200) or a range (200-299)',
    (value) => isValidStatusCodeOrRange(value)
  )
  .required('Please enter a value for a healthy status code')

const requestTimeoutSchema = yup.number().when('isCustomRequestTimeout', {
  is: true,
  then: (schema) =>
    schema
      .required('Request timeout is required')
      .min(
        MIN_OK_LATENCY_MS,
        `Request timeout must be between ${MIN_OK_LATENCY_MS} and ${MAX_OK_LATENCY_MS}`
      )
      .max(
        MAX_OK_LATENCY_MS,
        `Request timeout must be between ${MIN_OK_LATENCY_MS} and ${MAX_OK_LATENCY_MS}`
      )
      .typeError('Request timeout must be a number'),
  otherwise: (schema) => schema.nullable().notRequired()
})

function getMonitorUrlSchema(
  statusChecks: ServiceStatusCheckFromStatusCheck[],
  observabilityTool: ObservabilityTool,
  requestMethod: EndpointConfiguration['method'],
  isPrivateNetworkIntegration: boolean
) {
  const { integrationType, type } = observabilityTool

  const isAppdynamicsObservabilityTool = isObservabilityToolOfType(
    ObservabilityToolType.APPDYNAMICS,
    observabilityTool
  )
  const isCustomObservabilityTool = isObservabilityToolOfType(
    ObservabilityToolType.CUSTOM,
    observabilityTool
  )

  return yup
    .string()
    .trim()
    .required('Please enter a Monitor URL')
    .test(
      'is-monitor-url-valid',
      'Please enter a valid URL',
      (value: string) =>
        integrationType !== undefined &&
        validateMonitorUrl(value, integrationType, isPrivateNetworkIntegration)
    )
    .test(
      'is-monitor-url-already-in-use',
      'This Monitor URL is already in use for the selected Observability Tool',
      (value: string) => {
        // AppDynamics & Custom observability tool types do not have duplicate URL restrictions (always pass).
        if (isAppdynamicsObservabilityTool || isCustomObservabilityTool)
          return true

        const predicateFn = getExistingMonitorUrlPredicateFn(type)
        const transformUrlFn = getExistingMonitorUrlTransformFn(type)

        /*
         * Given the available Health Checks, the selected Observability Tool, and the method
         * (available in 'custom' observability tool types), map existing Health Checks to an array of URLs,
         * and apply filtering predicates and/or extraction transformations during the iteration.
         */
        const urls = extractUrlsFromStatusChecks(
          statusChecks,
          observabilityTool,
          requestMethod,
          predicateFn,
          transformUrlFn
        )

        // Ensure the user-provided value is not contained in the list of existing Health Check URLs
        return !urls.includes(value)
      }
    )
}

const dynatraceEntitySelectionSchema = yup.object({
  type: yup.mixed<DynatraceEntitySelectionSource>().required(),
  entityId: yup.string().when('type', {
    is: DynatraceEntitySelectionSource.ENTITY_ID,
    then: (schema) =>
      schema
        .test(
          'entity-id-format',
          'Value does not match the expected Dynatrace entity ID format',
          (value = '') => dynatraceEntityIdRegex.test(value)
        )
        .required(),
    otherwise: (schema) => schema.strip()
  }),
  k8sQuery: yup.object().when('type', {
    is: DynatraceEntitySelectionSource.K8S_TARGETING,
    then: (schema) =>
      schema.shape({
        clusterId: yup.string().required(),
        kind: yup.string().required(),
        name: yup.string().required(),
        namespace: yup.string().required()
      }),
    otherwise: (schema) => schema.strip()
  })
})

const grafanaSelectionSchema = yup.object({
  type: yup.mixed<GrafanaSelectionSource>().required()
})

const prometheusSelectionSchema = yup.object({
  type: yup.string().oneOf(Object.values(PrometheusSelectionSource)).required()
})

interface StatusCheckFormValidationSchemaOptions {
  categoryVisibilityState: StatusCheckCategoryFieldVisibilityState
  featureFlags: GremlinClientFlagsPartial
}

export function getStatusCheckFormValidationSchema(
  externalIntegrations: TeamExternalIntegration[],
  existingStatusChecks: ServiceStatusCheckFromStatusCheck[],
  options: StatusCheckFormValidationSchemaOptions
) {
  const { categoryVisibilityState } = options

  return yup.lazy((values: StatusCheckFormValues) => {
    const {
      dynatraceSelector,
      endpointConfiguration = {},
      isPrivateNetworkIntegration = false,
      observabilityTool
    } = values
    const { method } = endpointConfiguration

    // Category field flags
    const isCategoryFieldRequired =
      categoryVisibilityState ===
      StatusCheckCategoryFieldVisibilityState.REQUIRED

    // Observability Tool type flags
    const isCustomObservabilityTool = isObservabilityToolOfType(
      ObservabilityToolType.CUSTOM,
      observabilityTool
    )
    const isDynatraceObservabilityTool = isObservabilityToolOfType(
      ObservabilityToolType.DYNATRACE,
      observabilityTool
    )
    const isGrafanaObservabilityTool = isObservabilityToolOfType(
      ObservabilityToolType.GRAFANA,
      observabilityTool
    )
    const isPrometheusObservabilityTool = isObservabilityToolOfType(
      ObservabilityToolType.PROMETHEUS,
      observabilityTool
    )

    // Schema inclusion flags
    const shouldIncludeDynatraceSelectionSchema =
      isDynatraceObservabilityTool &&
      dynatraceSelector?.type !== DynatraceEntitySelectionSource.PROBLEMS_URL

    return yup.object().shape({
      // Category field is required for Dynatrace K8s Targeting and Entity ID input options
      category: yup
        .string()
        .oneOf(Object.values(StatusCheckCategory))
        .when({
          is: isCategoryFieldRequired,
          then: (schema) =>
            schema.required('Please select a category for this Health Check')
        }),
      ...(shouldIncludeDynatraceSelectionSchema
        ? // These fields are mutually exclusive in the Dynatrace Status Check case.
          { dynatraceSelector: dynatraceEntitySelectionSchema }
        : // Otherwise we are expecting a Monitor URL field to be present.
          // Dynatrace IHC case will have its `evaluationConfiguration` details constructed by the BE.
          {
            evaluationConfiguration: yup.object({
              healthyStatusCode: healthyStatusCodeSchema,
              isCustomRequestTimeout: yup.boolean().required(),
              requestTimeout: requestTimeoutSchema,
              responseBodyEvaluation: responseBodyEvaluationSchema
            }),
            healthyStatusCode: healthyStatusCodeSchema,
            monitorUrl: getMonitorUrlSchema(
              existingStatusChecks,
              observabilityTool,
              method,
              isPrivateNetworkIntegration
            )
          }),
      ...(isGrafanaObservabilityTool && {
        grafanaSelector: grafanaSelectionSchema
      }),
      ...(isPrometheusObservabilityTool && {
        prometheusSelector: prometheusSelectionSchema
      }),
      pollingIntervalSeconds: yup
        .number()
        .default(DEFAULT_POLL_INTERVAL)
        .min(
          MINIMUM_POLL_INTERVAL,
          `Polling interval must not be shorter than ${MINIMUM_POLL_INTERVAL} seconds`
        )
        .max(
          MAXIMUM_POLL_INTERVAL,
          `Polling interval must not be longer than ${MAXIMUM_POLL_INTERVAL} seconds`
        )
        .typeError('A polling interval is required'),
      // 'GET' \ 'POST' endpoint configuration applies exclusively to Custom observability tool types at this time.
      ...(isCustomObservabilityTool && {
        endpointConfiguration: yup.object().shape({
          method: yup
            .string()
            .test(
              'is-valid-method',
              'Value must be a valid request method',
              (value) => value === 'GET' || value === 'POST'
            )
            .required(),
          headers: yup.object().when('method', {
            is: (method: 'POST' | 'GET') => method === 'POST',
            then: (schema) =>
              schema.shape({
                ['content-type']: yup
                  .string()
                  .max(50, 'Content Types must be 50 characters or less')
                  .required(
                    'A ContentType header must be specified when using POST method'
                  )
              })
          }),
          payload: yup.string().when('method', {
            is: (method: 'POST' | 'GET') => method === 'POST',
            then: (schema) =>
              schema.required(
                'A Request Body must be specified when using POST method'
              )
          })
        })
      }),
      statusCheckName: yup
        .string()
        .trim()
        .when('observabilityTool', {
          is: (observabilityTool: ObservabilityTool) =>
            isSelectedObservabilityToolAuthenticated(
              observabilityTool,
              externalIntegrations
            ),
          then: (schema) =>
            schema
              .min(1, 'Health Check name cannot be empty')
              .required('Please enter a name for this Health Check')
        })
    })
  })
}
